import React, { useEffect, useRef, useState } from 'react';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'SGD'];
const BUDGET_TYPES = ['fixed', 'hourly', 'monthly'];

const ProjectBudgetForm = ({ budget, hourlyRate, onChange, autoFocusBudget = false }) => {
  const [budgetData, setBudgetData] = useState(budget || {});
  const [rateData, setRateData] = useState(hourlyRate || {});
  const [showBudget, setShowBudget] = useState(!!budget);
  const [showRate, setShowRate] = useState(!!hourlyRate);
  const timeBudgetRef = useRef(null);

  const handleBudgetChange = (field, value) => {
    const newBudget = { ...budgetData, [field]: value };
    setBudgetData(newBudget);
    onChange({ budget: newBudget, hourlyRate: rateData });
  };

  const handleRateChange = (field, value) => {
    const newRate = { ...rateData, [field]: value };
    setRateData(newRate);
    onChange({ budget: budgetData, hourlyRate: newRate });
  };

  const toggleBudget = () => {
    const newShowBudget = !showBudget;
    setShowBudget(newShowBudget);
    if (!newShowBudget) {
      setBudgetData({});
      onChange({ budget: null, hourlyRate: rateData });
    } else {
      const defaultBudget = {
        time_hours: 40,
        money_amount: 5000,
        currency: 'USD',
        type: 'fixed'
      };
      setBudgetData(defaultBudget);
      onChange({ budget: defaultBudget, hourlyRate: rateData });
    }
  };

  const hasAutoFocused = useRef(false);

  useEffect(() => {
    if (!autoFocusBudget || hasAutoFocused.current) return;

    if (!showBudget) {
      setShowBudget(true);
      if (!budgetData || Object.keys(budgetData).length === 0) {
        const defaultBudget = {
          time_hours: 40,
          money_amount: 5000,
          currency: 'USD',
          type: 'fixed'
        };
        setBudgetData(defaultBudget);
        onChange({ budget: defaultBudget, hourlyRate: rateData });
      }
    }

    const timeout = setTimeout(() => {
      timeBudgetRef.current?.focus();
    }, 0);

    hasAutoFocused.current = true;

    return () => clearTimeout(timeout);
  }, [autoFocusBudget, showBudget, budgetData, rateData, onChange]);

  const toggleRate = () => {
    const newShowRate = !showRate;
    setShowRate(newShowRate);
    if (!newShowRate) {
      setRateData({});
      onChange({ budget: budgetData, hourlyRate: null });
    } else {
      const defaultRate = {
        amount: 100,
        currency: 'USD',
        custom: true
      };
      setRateData(defaultRate);
      onChange({ budget: budgetData, hourlyRate: defaultRate });
    }
  };

  return (
    <div className="space-y-4">
      {/* Hourly Rate Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showRate}
              onChange={toggleRate}
              className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
            />
            Hourly Rate
          </label>
          {showRate && (
            <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">Track earnings</span>
          )}
        </div>

        {showRate && (
          <div className="grid grid-cols-2 gap-3 ml-6">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                Rate Amount
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rateData.amount || ''}
                onChange={(e) => handleRateChange('amount', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                placeholder="100.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                Currency
              </label>
              <select
                value={rateData.currency || 'USD'}
                onChange={(e) => handleRateChange('currency', e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Budget Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showBudget}
              onChange={toggleBudget}
              className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
            />
            Set Budget
          </label>
          {showBudget && (
            <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">Track progress & get alerts</span>
          )}
        </div>

        {showBudget && (
          <div className="space-y-3 ml-6">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                Budget Type
              </label>
              <select
                value={budgetData.type || 'fixed'}
                onChange={(e) => handleBudgetChange('type', e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
              >
                {BUDGET_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                  Time Budget (hours)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={budgetData.time_hours || ''}
                  onChange={(e) => handleBudgetChange('time_hours', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                  ref={timeBudgetRef}
                  placeholder="40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                  Money Budget
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetData.money_amount || ''}
                  onChange={(e) => handleBudgetChange('money_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                  placeholder="5000"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors">
                Currency
              </label>
              <select
                value={budgetData.currency || 'USD'}
                onChange={(e) => handleBudgetChange('currency', e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>

            {showRate && rateData.amount && budgetData.time_hours && (
              <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-3 transition-colors">
                <div className="text-xs text-cyan-700 dark:text-cyan-300 transition-colors">
                  <div className="font-semibold mb-1">Estimated Earnings:</div>
                  <div className="font-mono">
                    {budgetData.time_hours}h × {rateData.currency} {rateData.amount}/h = 
                    <span className="font-bold ml-1">
                      {rateData.currency} {(budgetData.time_hours * rateData.amount).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectBudgetForm;
